import React, { useState, useEffect } from 'react';
import { Form, Input, Select, DatePicker, Button, Row, Col, Space, Spin, message, Tabs } from 'antd';
import { PlusOutlined, MinusCircleOutlined } from '@ant-design/icons';
import { createShipment, updateShipment } from '../../../services/shipments';
import { useFormData } from '../../../context/FormDataContext';
import dayjs from 'dayjs';
import './ShipmentAddNowlun.scss';

// Custom option component for locations
const LocationOption = ({ data }) => {
  const isPort = data.type === 'port';
  const iconUrl = data.iconUrl;
  const flagUrl = data.imgUrl;
  const iconAlt = isPort ? 'Port' : 'Area';
  return (
    <div className="location-option">
      {iconUrl && <img src={iconUrl} alt={iconAlt} className="location-icon" />}
      {flagUrl && <img src={flagUrl} alt={data.display_country} className="country-flag" />}
      <div className="location-info">
        <div className="location-country">{data.code}</div>
        <div className="location-name">{data.display_name}</div>
      </div>
    </div>
  );
};

const FCLForm = ({ areasOptions, portsOptions, shippingLinesOptions, locationFilterOption, isEditMode }) => (
  <>
    <Row gutter={16}>
      <Col span={12}>
        <Form.Item
          label="Pickup Location"
          name="pickup_location_id"
        >
          <Select
            showSearch
            allowClear
            placeholder="Select pickup location"
            options={areasOptions}
            filterOption={locationFilterOption}
          />
        </Form.Item>
      </Col>

      <Col span={12}>
        <Form.Item
          label="Pickup Date"
          name="planned_pickup_at"
        >
          <DatePicker showTime format="YYYY-MM-DD HH:mm" style={{ width: '100%' }} placeholder="YYYY/MM/DD" />
        </Form.Item>
      </Col>
    </Row>

    <Row gutter={16}>
      <Col span={12}>
        <Form.Item
          label="Loading Port"
          name="loading_port_id"
          rules={[{ required: true, message: 'Please select the loading port' }]}
        >
          <Select
            showSearch
            allowClear
            placeholder="Select loading port"
            options={portsOptions}
            filterOption={locationFilterOption}
          />
        </Form.Item>
      </Col>

      <Col span={12}>
        <Form.Item
          label="ETD"
          name="planned_loading_port_at"
          rules={[{ required: true, message: 'Please select the ETD date' }]}
        >
          <DatePicker showTime format="YYYY-MM-DD HH:mm" style={{ width: '100%' }} placeholder="YYYY/MM/DD" />
        </Form.Item>
      </Col>
    </Row>

    <Row gutter={16}>
      <Col span={12}>
        <Form.Item
          label="Destination Port"
          name="destination_port_id"
          rules={[{ required: true, message: 'Please select the Destination port' }]}
        >
          <Select
            showSearch
            allowClear
            placeholder="Select destination port"
            options={portsOptions}
            filterOption={locationFilterOption}
          />
        </Form.Item>
      </Col>

      <Col span={12}>
        <Form.Item
          label="ETA"
          name="planned_destination_port_at"
          rules={[{ required: true, message: 'Please select the ETA date' }]}
        >
          <DatePicker showTime format="YYYY-MM-DD HH:mm" style={{ width: '100%' }} placeholder="YYYY/MM/DD" />
        </Form.Item>
      </Col>
    </Row>

    <Row gutter={16}>
      <Col span={12}>
        <Form.Item
          label="Dropoff Location"
          name="dropoff_location_id"
        >
          <Select
            showSearch
            allowClear
            placeholder="Select dropoff location"
            options={areasOptions}
            filterOption={locationFilterOption}
          />
        </Form.Item>
      </Col>

      <Col span={12}>
        <Form.Item
          label="Dropoff Date"
          name="planned_dropoff_at"
        >
          <DatePicker showTime format="YYYY-MM-DD HH:mm" style={{ width: '100%' }} placeholder="YYYY/MM/DD" />
        </Form.Item>
      </Col>
    </Row>

    <Row gutter={16}>
      <Col span={12}>
        <Form.Item
          label="Booking Number"
          name="booking_number"
        >
          <Input placeholder="Enter booking number" disabled={isEditMode} />
        </Form.Item>
      </Col>
      <Col span={12}>
        <Form.Item
          label="BL"
          name="bl"
        >
          <Input placeholder="Enter BL number" disabled={isEditMode} />
        </Form.Item>
      </Col>
    </Row>

    <Row gutter={16}>
      <Col span={12}>
        <Form.Item
          label="Shipment Reference"
          name="client_ref"
        >
          <Input placeholder="Enter client reference" />
        </Form.Item>
      </Col>
      <Col span={12}>
        <Form.Item
          label="Vessel"
          name="vessel_name"
        >
          <Input placeholder="Enter vessel name" />
        </Form.Item>
      </Col>
    </Row>

    <Row gutter={16}>
      <Col span={12}>
        <Form.Item
          label="Shipping Line"
          name="shipping_line_id"
        >
          <Select
            showSearch
            allowClear
            placeholder="Select shipping line"
            options={shippingLinesOptions}
            filterOption={(input, option) =>
              option.label.toLowerCase().indexOf(input?.toLowerCase()) >= 0
            }
            disabled={isEditMode}
          />
        </Form.Item>
      </Col>
    </Row>
  </>
);

const TruckingForm = ({ areasOptions, portsOptions, locationFilterOption }) => (
  <>
    <Row gutter={16}>
      <Col span={12}>
        <Form.Item
          label="Pickup Location"
          name="pickup_location_id"
          rules={[{ required: true, message: 'Please select the pickup location' }]}
        >
          <Select
            showSearch
            allowClear
            placeholder="Select pickup location"
            options={[...portsOptions, ...areasOptions]}
            filterOption={locationFilterOption}
          />
        </Form.Item>
      </Col>

      <Col span={12}>
        <Form.Item
          label="Pickup Date"
          name="planned_pickup_at"
          rules={[{ required: true, message: 'Please select the pickup Date' }]}
        >
          <DatePicker showTime format="YYYY-MM-DD HH:mm" style={{ width: '100%' }} placeholder="YYYY/MM/DD" />
        </Form.Item>
      </Col>
    </Row>

    <Row gutter={16}>
      <Col span={12}>
        <Form.Item
          label="Dropoff Location"
          name="dropoff_location_id"
          rules={[{ required: true, message: 'Please select the Dropoff Location' }]}
        >
          <Select
            showSearch
            allowClear
            placeholder="Select dropoff location"
            options={[...portsOptions, ...areasOptions]}
            filterOption={locationFilterOption}
          />
        </Form.Item>
      </Col>

      <Col span={12}>
        <Form.Item
          label="Dropoff Date"
          name="planned_dropoff_at"
          rules={[{ required: true, message: 'Please select the Dropoff Date' }]}
        >
          <DatePicker showTime format="YYYY-MM-DD HH:mm" style={{ width: '100%' }} placeholder="YYYY/MM/DD" />
        </Form.Item>
      </Col>
    </Row>
  </>
);

const ShipmentAddNowlun = ({ shipment, refetch, close }) => {
  const [form] = Form.useForm();
  const [activeTab, setActiveTab] = useState('fcl');
  const isEditMode = !!shipment;

  // Use the form data context
  const { areas, ports, containers, shippingLines, isLoading } = useFormData();

  const areasOptions = areas?.areas?.map(area => ({
    value: area.id,
    label: <LocationOption data={area} />,
    data: area,
  })) || [];

  const portsOptions = ports?.ports?.map(port => ({
    value: port.id,
    label: <LocationOption data={port} />,
    data: port,
  })) || [];

  const containersOptions = containers?.containers?.map(container => ({
    value: container.id,
    label: container.name,
  })) || [];

  const shippingLinesOptions = shippingLines?.lines?.map(line => ({
    value: line[0],
    label: line[1],
  })) || [];

  // Enhanced search function for locations
  const locationFilterOption = (input, option) => {
    const searchText = input.toLowerCase();
    const locationData = option.data;

    return (
      locationData.display_name?.toLowerCase()?.includes(searchText) ||
      locationData.display_country?.toLowerCase()?.includes(searchText) ||
      locationData.value?.toLowerCase()?.includes(searchText) ||
      locationData.value_ar?.toLowerCase()?.includes(searchText) ||
      locationData.code?.toLowerCase()?.includes(searchText) ||
      locationData.iso?.toLowerCase()?.includes(searchText) ||
      locationData.country?.toLowerCase()?.includes(searchText) ||
      locationData.country_ar?.toLowerCase()?.includes(searchText)
    );
  };

  // Helper function to parse date string to dayjs object
  const parseDate = (dateString) => {
    if (!dateString) return null;
    return dayjs(dateString, 'DD MMM YYYY');
  };

  // Set initial values when in edit mode and all data is loaded
  useEffect(() => {
    if (isEditMode && shipment && !isLoading) {
      form.setFieldsValue({
        pickup_location_id: shipment.pick?.id,
        planned_pickup_at: parseDate(shipment.pick?.date),
        loading_port_id: shipment.pol?.id,
        planned_loading_port_at: parseDate(shipment.pol?.date),
        destination_port_id: shipment.pod?.id,
        planned_destination_port_at: parseDate(shipment.pod?.date),
        dropoff_location_id: shipment.dropoff?.id,
        planned_dropoff_at: parseDate(shipment.dropoff?.date),
        booking_number: shipment.booking_number,
        bl: shipment.bl,
        shipping_line_id: shipment.shipping_line?.id,
        vessel: shipment.vessel,
        client_ref: shipment.client_ref,
        line_items_attributes: shipment.line_items?.map(item => ({
          container_type_id: item.container_type_id,
          amount: item.amount,
          id: item.id,
        })),
      });
    }
  }, [shipment, form, isEditMode, isLoading]);

  // Determine initial tab in edit mode
  useEffect(() => {
    if (isEditMode && shipment) {
      const isTrucking = shipment.pick?.id && shipment.dropoff?.id && !shipment.pol?.id && !shipment.pod?.id;
      setActiveTab(isTrucking ? 'trucking' : 'fcl');
    }
  }, [shipment, isEditMode]);

  const handleFinish = async (values) => {
    try {
      // Format dates to ISO string
      const formattedValues = {
        ...values,
        planned_pickup_at: values.planned_pickup_at?.format('YYYY-MM-DD HH:mm:ss'),
        planned_loading_port_at: values.planned_loading_port_at?.format('YYYY-MM-DD HH:mm:ss'),
        planned_destination_port_at: values.planned_destination_port_at?.format('YYYY-MM-DD HH:mm:ss'),
        planned_dropoff_at: values.planned_dropoff_at?.format('YYYY-MM-DD HH:mm:ss'),
      };

      const { data } = isEditMode
        ? await updateShipment(shipment.id, formattedValues)
        : await createShipment(formattedValues);

      message.success(`Shipment ${isEditMode ? 'updated' : 'created'} successfully`);

      form.resetFields();
      refetch();
      close();
    } catch (error) {
      message.error(error.response?.data?.errors || error.message);
    }
  };

  // Handle tab change
  const handleTabChange = (newTab) => {
    setActiveTab(newTab);
    form.resetFields();
  };

  if (isLoading) {
    return (
      <div className="ShipmentAddNowlun">
        <div className="loading-container">
          <div>Loading data...</div>
          <Spin size="small" />
        </div>
      </div>
    );
  }

  return (
    <div className="ShipmentAddNowlun">
      <div className="header">
        <div className="header__title">{isEditMode ? 'Update Shipment' : 'Add Nowlun Shipment'}</div>
        <div className="header__subtitle">
          {isEditMode ? 'Update shipment details' : 'Enter shipment details to create a new shipment'}
        </div>
      </div>

      <Form
        form={form}
        layout="vertical"
        onFinish={handleFinish}
        className="form"
      >
        {!isEditMode && (
          <Tabs
            activeKey={activeTab}
            onChange={handleTabChange}
            items={[
              {
                key: 'fcl',
                label: 'FCL',
                children: activeTab === 'fcl' ? (
                  <FCLForm
                    areasOptions={areasOptions}
                    portsOptions={portsOptions}
                    shippingLinesOptions={shippingLinesOptions}
                    locationFilterOption={locationFilterOption}
                    isEditMode={isEditMode}
                  />
                ) : null,
              },
              {
                key: 'trucking',
                label: 'Trucking',
                children: activeTab === 'trucking' ? (
                  <TruckingForm
                    areasOptions={areasOptions}
                    portsOptions={portsOptions}
                    locationFilterOption={locationFilterOption}
                  />
                ) : null,
              },
            ]}
          />
        )}
        {isEditMode && (
          activeTab === 'trucking' ?
            <TruckingForm
              areasOptions={areasOptions}
              portsOptions={portsOptions}
              locationFilterOption={locationFilterOption}
            /> :
            <FCLForm
              areasOptions={areasOptions}
              portsOptions={portsOptions}
              shippingLinesOptions={shippingLinesOptions}
              locationFilterOption={locationFilterOption}
              isEditMode={isEditMode}
            />
        )}

        <div className="line-items-section">
          <div className="section-header">
            <h3>Containers</h3>
            <p>Add containers for this shipment</p>
          </div>

          <Form.List name="line_items_attributes">
            {(fields, { add, remove }) => (
              <>
                {fields.map(({ key, name, ...restField }) => (
                  <Row gutter={16} key={key} className="line-item-row">
                    <Col span={10}>
                      <Form.Item
                        {...restField}
                        name={[name, 'container_type_id']}
                        rules={[{ required: true, message: 'Please select container type' }]}
                      >
                        <Select
                          allowClear
                          placeholder="Select container type"
                          options={containersOptions}
                        />
                      </Form.Item>
                    </Col>
                    <Col span={10}>
                      <Form.Item
                        {...restField}
                        name={[name, 'amount']}
                        rules={[{ required: true, message: 'Please enter amount' }]}
                      >
                        <Input type="number" placeholder="Enter amount" min={1} />
                      </Form.Item>
                    </Col>
                    <Col span={4}>
                      <MinusCircleOutlined onClick={() => remove(name)} className="remove-button" />
                    </Col>
                    <Form.Item
                      {...restField}
                      name={[name, 'id']}
                      hidden
                    >
                      <Input />
                    </Form.Item>
                  </Row>
                ))}
                <Form.Item>
                  <Button
                    type="dashed"
                    onClick={() => add()}
                    block
                    icon={<PlusOutlined />}
                  >
                    Add Container
                  </Button>
                </Form.Item>
              </>
            )}
          </Form.List>
        </div>

        <Form.Item>
          <Button type="primary" htmlType="submit" className="bold-btn">
            {isEditMode ? 'Update Shipment' : 'Create Shipment'}
          </Button>
        </Form.Item>
      </Form>
    </div>
  );
};

export default ShipmentAddNowlun;
